import {
    faEye,
    faEyeSlash,
    faNoteSticky,
    faPenToSquare,
    faTag,
    faTriangleExclamation,
    faUser,
} from '@fortawesome/pro-regular-svg-icons';
import {isEqual} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {appRoute} from '../../../../app-route';
import {CursorPaginator} from '../../../../component/cursor-paginator/cursor-paginator';
import {Meta} from '../../../../component/meta/meta';
import {ActivationPage} from '../../../../layout/activation-page/activation-page';
import {Page} from '../../../../layout/page/page';
import {MainPageContainer} from '../../../../layout/page-card/main-page-card';
import {useDocumentationLinks} from '../../../../provider/help-links/help-links-hook';
import {useLicenses} from '../../../../provider/license/license-hook';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {Locale} from '../../../../provider/locale/localization';
import {useModal} from '../../../../provider/modal/modal-hook';
import {useSystem} from '../../../../provider/system/system-hook';
import {ReviewCatalogType} from '../../../../service/reviews/reviews-catalogs';
import {useBulkToggleReviewVisibilityMutation} from '../../../../service/reviews/reviews-hide';
import {NoteFormType} from '../../../../service/reviews/reviews-notes';
import {useReviewStatusList} from '../../../../service/reviews/reviews-statuses';
import {TagDataType} from '../../../../service/reviews/reviews-tags';
import {ReviewDataType, ReviewSortingTypeEnum} from '../../../../service/reviews/reviews-type';
import {FeaturesEnum} from '../../../../service/user/user-type';
import {useSelectedItems} from '../../../../util/selected-items/use-selected-items';
import {useUrl} from '../../../../util/url-hook/url-hook';
import {reviewPageSizeOptions} from '../reviews-const';
import {ReviewsHeader} from '../reviews-header/reviews-header';
import {isReviewAbuseAllowed, isReviewReplyAllowed} from '../reviews-helper';
import {useReviewStateHook} from '../reviews-state/reviews-state-provider';
import {ReviewsStateType, ReviewsStateUrlQueryNameEnum} from '../reviews-state/reviews-state-type';
import {TagsPopup} from '../tags-popup/tags-popup';

import {AppstoreCredentialsUpdateModal} from './appstore-credentials-update-modal/appstore-credentials-update-modal';
import {BulkReviewNoteModal} from './bulk-review-note-modal/bulk-review-note-modal';
import {NeuralNetworkAlert} from './neuralink-alert/neuralink-alert';
import {NewReviewList} from './review-list/new-review-list';
import {BulkAbuseModal} from './review-list/review/review-header/bulk-abuse-modal/bulk-abuse-modal';
import {BulkAnswerModal} from './review-list/review/review-header/bulk-answer-modal/bulk-answer-modal';
import {ResponsibleUserModal} from './review-list/review/review-header/responsible-user-modal/responsible-user-modal';
import {StatusChangeModal} from './review-list/review/review-header/status-change-modal/status-change-modal';
import {ReviewDataWithCatalogType} from './review-list/review-list-type';
import {MenuItemConfigType, ReviewsBulkSelectionBar} from './reviews-bulk-selection-bar/reviews-bulk-selection-bar';
import {ReviewsFilter} from './reviews-filter/reviews-filter';
import {ReviewsFilterDataType} from './reviews-filter/reviews-filter-type';
import {useBulkReviewNotesApplyMutation} from './use-bulk-review-notes-apply-mutation';
import {useBulkReviewTagsApplyMutation} from './use-bulk-review-tags-apply-mutation';
import * as styles from './review-list-page-content.scss';

function resolveReviewFromList(
    review: ReviewDataWithCatalogType,
    reviewsResults: Array<ReviewDataType> | undefined,
    catalogListMap: Record<number, ReviewCatalogType>
): ReviewDataWithCatalogType {
    const fresh = reviewsResults?.find((item) => item.id === review.id);

    if (!fresh) {
        return review;
    }

    return {
        ...fresh,
        catalog: catalogListMap[fresh.catalogId] || null,
    };
}

// eslint-disable-next-line complexity, max-statements
export function ReviewListPageContent(): JSX.Element {
    const [isFilterOpened, setIsFilterOpened] = useState(true);
    const {
        reviews,
        cursorPagination,
        isReviewsInProgress,
        updateReview,
        refreshReview,
        refreshReviews,
        refreshReviewsInfo,
        reviewsState,
        reviewsCatalogList,
    } = useReviewStateHook();
    const {getLocalizedString, localeName} = useLocale();
    const {modal} = useModal();
    const {licenses} = useLicenses();
    const systemContext = useSystem();
    const {screen} = systemContext;
    const {isMobile} = screen;
    const {featureActivation} = useDocumentationLinks();
    const {replaceQuery: setReviewsStateUrlQuery, queries} = useUrl<
        Omit<ReviewsStateType, 'filterState'> & ReviewsFilterDataType
    >();

    const [isOpenResponsibleUserModal, setIsOpenResponsibleUserModal] = useState<boolean>(false);
    const [isOpenStatusChangeModal, setIsOpenStatusChangeModal] = useState<boolean>(false);
    const [isOpenBulkAnswerModal, setIsOpenBulkAnswerModal] = useState<boolean>(false);
    const [isOpenBulkAbuseModal, setIsOpenBulkAbuseModal] = useState<boolean>(false);
    const [isOpenBulkTagsModal, setIsOpenBulkTagsModal] = useState<boolean>(false);
    const [isOpenBulkNotesModal, setIsOpenBulkNotesModal] = useState<boolean>(false);

    const hasReviews = useMemo(() => Boolean(reviews?.results.length), [reviews]);
    const total = reviews?.results.length ?? 0;

    const {selectedCount, isRowSelected, toggleRow, clear, pruneSelected, selectedKeys, isInverted} =
        useSelectedItems<ReviewDataWithCatalogType>(total);

    const isSelectMode = selectedCount > 0;

    const handleBulkAction = useCallback(() => {
        setIsOpenResponsibleUserModal(true);
    }, []);

    const handleCloseBulkModal = useCallback(() => {
        clear();
    }, [clear]);

    const handleBulkTagsFlowComplete = useCallback(() => {
        setIsOpenBulkTagsModal(false);
        handleCloseBulkModal();
    }, [handleCloseBulkModal]);

    const handleBulkNotesFlowComplete = useCallback(() => {
        setIsOpenBulkNotesModal(false);
        handleCloseBulkModal();
    }, [handleCloseBulkModal]);

    const {isLoading: isBulkTagsMutationLoading, mutateAsync: applyBulkReviewTags} = useBulkReviewTagsApplyMutation({
        onFlowComplete: handleBulkTagsFlowComplete,
        updateReview,
    });

    const {isLoading: isBulkNotesMutationLoading, mutateAsync: applyBulkReviewNotes} = useBulkReviewNotesApplyMutation({
        refreshReviewsInfo,
        onFlowComplete: handleBulkNotesFlowComplete,
    });

    const {
        mutateAsync: bulkToggleReviewVisibility,
        isLoading: isBulkVisibilityLoading,
        variables: bulkVisibilityVariables,
    } = useBulkToggleReviewVisibilityMutation({
        refreshReviews,
        updateReview,
        onFlowComplete: handleCloseBulkModal,
    });

    const isBulkUnhideInProgress = isBulkVisibilityLoading && bulkVisibilityVariables?.isHidden === false;

    const isReviewSelected = useCallback((review: ReviewDataWithCatalogType) => isRowSelected(review), [isRowSelected]);

    const toggleReviewSelection = useCallback((review: ReviewDataWithCatalogType) => toggleRow(review), [toggleRow]);

    const newFilterComponent = useMemo(
        () => <ReviewsHeader hasReviews={hasReviews} setIsFilterOpened={setIsFilterOpened} />,
        [hasReviews]
    );

    useEffect(() => {
        // eslint-disable-next-line no-undefined
        if (queries.ordering === undefined) {
            setReviewsStateUrlQuery({
                [ReviewsStateUrlQueryNameEnum.sorting]: ReviewSortingTypeEnum.acs,
            });
        }
    }, [queries.ordering, setReviewsStateUrlQuery]);

    useEffect(() => {
        setIsFilterOpened(!isMobile);
    }, [isMobile]);

    const previousFilterStateRef = useRef(reviewsState?.filterState);
    const shouldPruneSelectionAfterFilterRef = useRef(false);
    const visibleReviewIds = useMemo(
        () => new Set((reviews?.results ?? []).map((review) => review.id)),
        [reviews?.results]
    );
    const isVisibleSelectedReview = useCallback(
        (key: ReviewDataWithCatalogType) => visibleReviewIds.has(key.id),
        [visibleReviewIds]
    );

    useEffect(() => {
        const currentFilterState = reviewsState?.filterState;
        const previousFilterState = previousFilterStateRef.current;

        if (previousFilterState && currentFilterState && !isEqual(previousFilterState, currentFilterState)) {
            shouldPruneSelectionAfterFilterRef.current = true;
        }

        previousFilterStateRef.current = currentFilterState;
    }, [reviewsState?.filterState]);

    useEffect(() => {
        if (shouldPruneSelectionAfterFilterRef.current) {
            pruneSelected(isVisibleSelectedReview);
            shouldPruneSelectionAfterFilterRef.current = false;
        }
    }, [isVisibleSelectedReview, pruneSelected]);

    const catalogListMap = useMemo(() => {
        if (!reviewsCatalogList) {
            return {};
        }

        return reviewsCatalogList.reduce((accumulator, current) => {
            return {
                ...accumulator,
                [current.id]: current,
            };
        }, {} as Record<number, ReviewCatalogType>);
    }, [reviewsCatalogList]);

    const allSelectedReviews = useMemo(() => {
        const fullObjects = selectedKeys.filter(
            (key): key is ReviewDataWithCatalogType => typeof key === 'object' && key !== null && 'id' in key
        );

        if (isInverted) {
            if (!reviews?.results) {
                return [];
            }

            const reviewsWithCatalog: Array<ReviewDataWithCatalogType> = reviews.results.map((review) => ({
                ...review,
                catalog: catalogListMap[review.catalogId] || null,
            }));

            const excludedIds = new Set(fullObjects.map((key) => key.id));

            return reviewsWithCatalog.filter((review) => !excludedIds.has(review.id));
        }

        return fullObjects.map((review) => resolveReviewFromList(review, reviews?.results, catalogListMap));
    }, [isInverted, selectedKeys, reviews?.results, catalogListMap]);

    const handleBulkTagsApply = useCallback(
        async (selectedTags: Array<TagDataType>) => {
            if (allSelectedReviews.length === 0) {
                return;
            }

            await applyBulkReviewTags({
                listPageReviewsSnapshot: reviews?.results ?? [],
                reviewIds: allSelectedReviews.map((review) => review.id),
                selectedReviewsSnapshot: allSelectedReviews,
                selectedTags,
                tagIds: selectedTags.map((tag) => tag.id),
            });
        },
        [allSelectedReviews, applyBulkReviewTags, reviews?.results]
    );

    const handleBulkNotesSubmit = useCallback(
        async (formValues: NoteFormType) => {
            if (allSelectedReviews.length === 0) {
                return;
            }

            const text = (formValues.text ?? '').trim();

            await applyBulkReviewNotes({
                reviewIds: allSelectedReviews.map((review) => review.id),
                text,
            });
        },
        [allSelectedReviews, applyBulkReviewNotes]
    );

    const currentPageReviews = useMemo(() => {
        if (!reviews?.results) {
            return [];
        }

        return reviews.results.map((review) => ({
            ...review,
            catalog: catalogListMap[review.catalogId] || null,
        }));
    }, [reviews?.results, catalogListMap]);

    const allCurrentPageSelected = useMemo(() => {
        if (currentPageReviews.length === 0) {
            return false;
        }

        return currentPageReviews.every((review) => isRowSelected(review));
    }, [currentPageReviews, isRowSelected]);

    const toggleAllCurrentPage = useCallback(() => {
        if (currentPageReviews.length === 0) {
            return;
        }

        if (allCurrentPageSelected) {
            currentPageReviews.forEach((review) => {
                if (isRowSelected(review)) {
                    toggleRow(review);
                }
            });
        } else {
            currentPageReviews.forEach((review) => {
                if (!isRowSelected(review)) {
                    toggleRow(review);
                }
            });
        }
    }, [currentPageReviews, allCurrentPageSelected, isRowSelected, toggleRow]);

    const hasAbuseableSelected = useMemo(() => allSelectedReviews.some(isReviewAbuseAllowed), [allSelectedReviews]);

    const hasAnswerableSelected = useMemo(() => allSelectedReviews.some(isReviewReplyAllowed), [allSelectedReviews]);

    const hasHideableSelected = useMemo(
        () => allSelectedReviews.some((review) => !review.deletedAt && !review.isHidden),
        [allSelectedReviews]
    );

    const hasUnhideableSelected = useMemo(
        () => allSelectedReviews.some((review) => !review.deletedAt && review.isHidden),
        [allSelectedReviews]
    );

    const handleBulkHideReviews = useCallback(() => {
        const reviewIds = allSelectedReviews
            .filter((review) => !review.deletedAt && !review.isHidden)
            .map((review) => review.id);

        if (reviewIds.length === 0) {
            return;
        }

        modal.confirm({
            title: <Locale stringKey="REVIEWS__BULK_HIDE__MODAL_TITLE" />,
            content: <Locale stringKey="REVIEWS__BULK_HIDE__MODAL_CONTENT" />,
            onOk: () => bulkToggleReviewVisibility({reviewIds, isHidden: true}),
            okText: getLocalizedString('REVIEWS__REVIEW_HIDE__MODAL_OK'),
            cancelText: getLocalizedString('POPUP__BUTTON__CANCEL'),
        });
    }, [allSelectedReviews, bulkToggleReviewVisibility, getLocalizedString, modal]);

    const handleBulkUnhideReviews = useCallback(async () => {
        const reviewIds = allSelectedReviews
            .filter((review) => !review.deletedAt && review.isHidden)
            .map((review) => review.id);

        if (reviewIds.length === 0) {
            return;
        }

        await bulkToggleReviewVisibility({reviewIds, isHidden: false});
    }, [allSelectedReviews, bulkToggleReviewVisibility]);

    const {data: reviewStatusList} = useReviewStatusList(localeName);

    const allSelectedStatusChangePending = useMemo(() => {
        if (allSelectedReviews.length === 0 || !reviewStatusList?.statuses) {
            return false;
        }

        return allSelectedReviews.every(
            (review) => !reviewStatusList.statuses.some((statusItem) => statusItem.statusCode === review.processStatus)
        );
    }, [allSelectedReviews, reviewStatusList?.statuses]);

    const bulkSelectionBarMenuItems = useMemo((): Array<MenuItemConfigType> => {
        const items: Array<MenuItemConfigType> = [
            {
                icon: faPenToSquare,
                langKey: 'REVIEWS__BULK_SELECTION__CHANGE_STATUS',
                onClick: () => setIsOpenStatusChangeModal(true),
                disabled: allSelectedStatusChangePending,
                disabledTooltipLangKey: 'REVIEWS__BULK_STATUS__ALL_REPLACING_TOOLTIP',
            },
            {
                icon: faUser,
                langKey: 'REVIEW__DROPDOWN_MENU__APPOINT_RESPONSIBLE',
                onClick: handleBulkAction,
            },
            {
                icon: faTag,
                langKey: 'REVIEWS__BULK_SELECTION__ADD_TAG',
                onClick: () => setIsOpenBulkTagsModal(true),
            },
            {
                icon: faNoteSticky,
                langKey: 'REVIEW__DROPDOWN_MENU__ADD_NOTE',
                onClick: () => setIsOpenBulkNotesModal(true),
            },
        ];

        if (hasHideableSelected) {
            items.push({
                icon: faEyeSlash,
                langKey: 'REVIEWS__BULK_HIDE__HINT',
                onClick: handleBulkHideReviews,
            });
        }

        if (hasUnhideableSelected) {
            items.push({
                icon: faEye,
                langKey: 'REVIEWS__BULK_UNHIDE__HINT',
                onClick: handleBulkUnhideReviews,
            });
        }

        items.push({
            icon: faTriangleExclamation,
            langKey: 'REVIEWS__BULK_SELECTION__COMPLAIN',
            onClick: () => setIsOpenBulkAbuseModal(true),
            disabled: !hasAbuseableSelected,
            disabledTooltipLangKey: 'REVIEWS__BULK_ABUSE__DISABLED_TOOLTIP',
        });

        return items;
    }, [
        allSelectedStatusChangePending,
        handleBulkAction,
        handleBulkHideReviews,
        handleBulkUnhideReviews,
        hasAbuseableSelected,
        hasHideableSelected,
        hasUnhideableSelected,
    ]);

    if (!licenses[FeaturesEnum.reviews]?.isActive) {
        return (
            <ActivationPage
                breadCrumbList={[
                    {
                        path: appRoute.reviewsManagement.path,
                        titleLangKey: 'CATEGORY_NAME__REVIEWS',
                    },
                ]}
                interestedMessageLangKey="ACTIVATION_PAGE__INTERESTED_CONTACT_YOUR_MANAGER_TO_ACTIVATE_THIS_SERVICE"
                link={featureActivation[FeaturesEnum.reviews]}
                title={<Locale stringKey="CATEGORY_NAME__REVIEWS" />}
            />
        );
    }

    // eslint-disable-next-line no-undefined
    const firstSelectedReview = allSelectedReviews.length > 0 ? allSelectedReviews[0] : undefined;

    return (
        <Page
            renderContainer={(children) => (
                <MainPageContainer className={styles.Reviews_pageContainer}>{children}</MainPageContainer>
            )}
        >
            <Meta title={getLocalizedString('CATEGORY_NAME__REVIEWS')} />
            {newFilterComponent}
            <NeuralNetworkAlert />
            <AppstoreCredentialsUpdateModal />
            <div className={styles.Reviews_contentWrapper}>
                <div className={styles.Reviews_list}>
                    <NewReviewList isRowSelected={isReviewSelected} toggleRow={toggleReviewSelection} />
                    {hasReviews && !isReviewsInProgress && (
                        <CursorPaginator cursorPagination={cursorPagination} pageSizeOptions={reviewPageSizeOptions} />
                    )}
                    <ReviewsBulkSelectionBar
                        isActionInProgress={isBulkUnhideInProgress}
                        isVisible={isSelectMode}
                        menuItems={bulkSelectionBarMenuItems}
                        onCancel={clear}
                        selectedCount={selectedCount}
                        submitButtonConfig={{
                            disabled: !hasAnswerableSelected,
                            disabledTooltipLangKey: 'REVIEWS__BULK_ANSWER__DISABLED_TOOLTIP',
                            langKey: 'REVIEWS__REPLY',
                            onClick: () => setIsOpenBulkAnswerModal(true),
                        }}
                        toggleAllButtonConfig={{
                            langKey: allCurrentPageSelected ? 'TEXT__CANCEL_SELECT' : 'TEXT__SELECT_ALL',
                            onClick: toggleAllCurrentPage,
                        }}
                    />
                </div>

                <div className={styles.Reviews_filter} hidden={!isFilterOpened}>
                    <ReviewsFilter />
                </div>
            </div>
            {isOpenResponsibleUserModal && firstSelectedReview && (
                <ResponsibleUserModal
                    onCancel={() => setIsOpenResponsibleUserModal(false)}
                    onSuccess={() => {
                        setIsOpenResponsibleUserModal(false);
                        handleCloseBulkModal();
                    }}
                    reviewData={firstSelectedReview}
                    reviewId={firstSelectedReview.id}
                    selectedReviews={allSelectedReviews}
                    updateReview={updateReview}
                />
            )}
            {isOpenStatusChangeModal && allSelectedReviews.length > 0 && (
                <StatusChangeModal
                    onCancel={() => setIsOpenStatusChangeModal(false)}
                    onSuccess={() => {
                        setIsOpenStatusChangeModal(false);
                        handleCloseBulkModal();
                    }}
                    selectedReviews={allSelectedReviews}
                    updateReview={updateReview}
                />
            )}
            {isOpenBulkAnswerModal && allSelectedReviews.length > 0 && (
                <BulkAnswerModal
                    onCancel={() => setIsOpenBulkAnswerModal(false)}
                    onSuccess={() => {
                        setIsOpenBulkAnswerModal(false);
                        handleCloseBulkModal();
                    }}
                    refreshReview={refreshReview}
                    selectedReviews={allSelectedReviews}
                    updateReview={updateReview}
                />
            )}
            {isOpenBulkAbuseModal && allSelectedReviews.length > 0 && (
                <BulkAbuseModal
                    onCancel={() => setIsOpenBulkAbuseModal(false)}
                    onSuccess={() => {
                        setIsOpenBulkAbuseModal(false);
                        handleCloseBulkModal();
                    }}
                    refreshReview={refreshReview}
                    selectedReviews={allSelectedReviews}
                    updateReview={updateReview}
                />
            )}
            {isOpenBulkTagsModal && allSelectedReviews.length > 0 && (
                <TagsPopup
                    currentTags={[]}
                    isCreating={isBulkTagsMutationLoading}
                    isOpen
                    onApply={handleBulkTagsApply}
                    onClose={() => setIsOpenBulkTagsModal(false)}
                    title={<Locale stringKey="REVIEWS__TAGS_POPUP__BULK_HEADER" />}
                    variant="bulk"
                />
            )}
            {isOpenBulkNotesModal && allSelectedReviews.length > 0 && (
                <BulkReviewNoteModal
                    isLoading={isBulkNotesMutationLoading}
                    onCancel={() => setIsOpenBulkNotesModal(false)}
                    onSubmit={handleBulkNotesSubmit}
                />
            )}
        </Page>
    );
}
